"use client";

import { createContext, useContext, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useMe, isPublicPath } from "@/api/auth.api";
import type {
  AcademicYear,
  MeProfile,
  MeRole,
  MeSchool,
  MeUser,
} from "@/api/auth.api";
// ----------------------------------------
// Shape of what every component gets from useAuth()
// ----------------------------------------
interface AuthContextType {
  user: MeUser | null;
  academicYear: AcademicYear | null;
  role: MeRole | null;
  profile: MeProfile | null;
  school: MeSchool | null;
  permissions: string[];
  roleName: string | null;
  userId: number | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isError: boolean;

  // Permission gating helpers - UI visibility only.
  // Real authorization is always enforced server-side.
  hasPermission: (key: string | string[], mode?: "any" | "all") => boolean;
  hasModule: (module: string) => boolean;

  // Legacy no-ops - kept so old pages don't crash at compile time
  setRoles: (role: any) => void;
  setUserId: (id: any) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ----------------------------------------
// Provider
// ----------------------------------------
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();

  // Single source of truth for "is this path public" - same function
  // useMe() uses internally to decide `enabled`.
  const isPublic = isPublicPath(pathname);

  const { data, isLoading, isSuccess, isError } = useMe(pathname);

  const permissions = useMemo(
    () =>
      data?.permissions
        ? Object.values(data.permissions).flat()
        : [],
    [data?.permissions],
  );

  // Set for O(1) lookups - permissions arrays can be 25-30+ entries
  // and hasPermission gets called from many components on every render.
  const permSet = useMemo(() => new Set(permissions), [permissions]);
  const moduleSet = useMemo(
    () => new Set(data?.school.subscribedModules ?? []),
    [data?.school.subscribedModules],
  );

  const shouldBlock = !isPublic && isLoading;

  const value = useMemo<AuthContextType>(
    () => ({
      user: data?.user ?? null,
      role: data?.role ?? null,
      profile: data?.profile ?? null,
      school: data?.school ?? null,
      academicYear: data?.academicYear ?? null,
      permissions,
      roleName: data?.role?.name ?? null,
      userId:
        typeof data?.user?.id === "number"
          ? data.user.id
          : Number(data?.user?.id ?? 0) || null,
      isLoading: shouldBlock,
      isAuthenticated: isSuccess && !!data,
      isError,
      hasPermission: (key, mode = "any") => {
        const keys = Array.isArray(key) ? key : [key];
        return mode === "any"
          ? keys.some((k) => permSet.has(k))
          : keys.every((k) => permSet.has(k));
      },
      hasModule: (module) => moduleSet.has(module),
      setRoles: () => {},
      setUserId: () => {},
    }),
    [data, permissions, permSet, moduleSet, shouldBlock, isSuccess, isError],
  );

  // Block protected pages while /me is loading.
  // Remove this if you prefer per-page skeleton loaders instead.
  if (shouldBlock) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "#f9fafb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 36,
              height: 36,
              border: "4px solid #4F46E5",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.75s linear infinite",
              margin: "0 auto",
            }}
          />
          <p style={{ marginTop: 12, color: "#6B7280", fontSize: 14 }}>
            Loading...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ----------------------------------------
// Hook
// ----------------------------------------
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
